import React from "react";
import {
  Collapse,
  Divider,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Fade,
  Zoom,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import {
  styled,
  useTheme,
  Theme,
  CSSObject,
  keyframes,
} from "@mui/material/styles";
import { drawerWidth } from "../appbar/appbar";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "../../css/drawer.css";
import { DynamicIcon } from "../../../libs/helpers";
import { ModuleI } from "../../../libs/interfaces";
import { Button as ButtonAntd } from "antd";
import { useRouterStore } from "../../../libs/stores";
import {
  COLOR_PRIMARY,
  COLOR_SECONDARY,
  NAVBAR_HEIGHT,
} from "../../../libs/constants";
import { useWidth } from "../../../libs/hooks";
import { HomeOutlined } from "@ant-design/icons";

// Animaciones personalizadas
const pulseEffect = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    transform: translateX(-20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const expandEffect = keyframes`
  0% {
    transform: scaleY(0);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: scaleY(1);
    opacity: 1;
  }
`;

const glowEffect = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(25, 118, 210, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(25, 118, 210, 0.6), 0 0 30px rgba(25, 118, 210, 0.4);
  }
  100% {
    box-shadow: 0 0 5px rgba(25, 118, 210, 0.3);
  }
`;

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: 0,
  justifyContent: "center",
  height: `57px`,
  minHeight: `57px`,
  maxHeight: `57px`,
  backgroundColor: COLOR_PRIMARY,
  color: "white",
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(78px + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(78px + 1px)`,
  },
});

const DrawerEl = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  zIndex: 998,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: "all .2s ease-in-out",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// Styled component para el módulo con efectos
const StyledModuleItem = styled(ListItem, {
  shouldForwardProp: (prop) =>
    !["isExpanded", "isChildActive"].includes(prop as string),
})<{ isExpanded: boolean; isChildActive: boolean }>(
  ({ theme, isExpanded, isChildActive }) => ({
    backgroundColor: isChildActive
      ? COLOR_SECONDARY
      : isExpanded
      ? "#808080"
      : "white",
    color: isExpanded || isChildActive ? "white" : COLOR_PRIMARY,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    transform: isExpanded ? "scale(1.02)" : "scale(1)",
    animation: isExpanded ? `${pulseEffect} 1s ease-in-out` : "none",
    textDecoration: "none",
    "&:hover": {
      backgroundColor: isChildActive
        ? COLOR_SECONDARY
        : isExpanded
        ? "#606060"
        : "#f5f5f5",
      transform: "scale(1.01)",
      animation: `${glowEffect} 2s infinite`,
    },

    "& .MuiListItemButton-root": {
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  })
);

// Styled component para los elementos hijos con animación
const StyledChildrenContainer = styled(Box)(({ theme }) => ({
  "& .MuiListItem-root": {
    animation: `${slideInFromLeft} 0.4s ease-out`,
    "&:nth-of-type(1)": { animationDelay: "0.1s" },
    "&:nth-of-type(2)": { animationDelay: "0.2s" },
    "&:nth-of-type(3)": { animationDelay: "0.3s" },
    "&:nth-of-type(4)": { animationDelay: "0.4s" },
    "&:nth-of-type(5)": { animationDelay: "0.5s" },
  },
}));

// Componente para el icono de expansión con animación
const ExpandIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>(({ isExpanded }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
  marginLeft: "auto",
  marginRight: 8,
}));

interface PropsDrawerI {
  open: boolean;
  handleClose: () => void;
}

interface PropsI {
  data: ModuleI;
  padding?: number;
  open: boolean;
}

// Context para manejar el estado de los módulos expandidos
const DrawerStateContext = React.createContext<{
  expandedModules: Set<number>;
  toggleModule: (moduleId: number) => void;
  isModuleExpanded: (moduleId: number) => boolean;
  expandingModule: number | null;
}>({
  expandedModules: new Set(),
  toggleModule: () => {},
  isModuleExpanded: () => false,
  expandingModule: null,
});

export const Drawer = ({ open, handleClose }: PropsDrawerI) => {
  const width = useWidth();
  const theme = useTheme();
  const navigate = useNavigate();

  // OPTIMIZACIÓN: Solo obtener modules una vez y usar shallow comparison
  const modules = useRouterStore((state) => state.modules);

  const location = useLocation();

  // OPTIMIZACIÓN: Memoizar la inicialización del estado
  const [expandedModules, setExpandedModules] = React.useState<Set<number>>(
    () => {
      const saved = localStorage.getItem("drawer-expanded-modules");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    }
  );

  // Estado para controlar qué módulo se está expandiendo
  const [expandingModule, setExpandingModule] = React.useState<number | null>(
    null
  );

  // OPTIMIZACIÓN: Usar useCallback y dependencias específicas
  const initializeActiveModules = React.useCallback(() => {
    if (modules.length === 0) return;

    const activeModuleIds = modules
      .filter((module) =>
        module.children?.some(
          (child) => child.to && location.pathname.startsWith(child.to)
        )
      )
      .map((module) => module.id);

    if (activeModuleIds.length > 0) {
      setExpandedModules((prev) => {
        const newSet = new Set(prev);
        let hasChanges = false;

        activeModuleIds.forEach((id) => {
          if (!newSet.has(id)) {
            newSet.add(id);
            hasChanges = true;
          }
        });

        return hasChanges ? newSet : prev;
      });
    }
  }, [modules, location.pathname]);

  // OPTIMIZACIÓN: Solo ejecutar cuando cambien modules o pathname
  React.useEffect(() => {
    initializeActiveModules();
  }, [initializeActiveModules]);

  // OPTIMIZACIÓN: Usar useCallback para evitar re-renders
  const toggleModule = React.useCallback((moduleId: number) => {
    setExpandingModule(moduleId);

    // Resetear el estado de expansión después de un breve delay
    const timer = setTimeout(() => setExpandingModule(null), 600);

    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      // Guardar en localStorage
      localStorage.setItem(
        "drawer-expanded-modules",
        JSON.stringify(Array.from(newSet))
      );
      return newSet;
    });

    return () => clearTimeout(timer);
  }, []);

  const isModuleExpanded = React.useCallback(
    (moduleId: number) => {
      return expandedModules.has(moduleId);
    },
    [expandedModules]
  );

  // OPTIMIZACIÓN: Memoizar el context value
  const contextValue = React.useMemo(
    () => ({
      expandedModules,
      toggleModule,
      isModuleExpanded,
      expandingModule,
    }),
    [expandedModules, toggleModule, isModuleExpanded, expandingModule]
  );

  const handleNavigate = React.useCallback(() => {
    navigate("/dashboard");
    handleClose();
  }, [navigate, handleClose]);

  // OPTIMIZACIÓN: Memoizar BodyDrawer
  const BodyDrawer = React.useMemo(
    () => (
      <>
        <DrawerHeader>
          <ButtonAntd
            onClick={handleClose}
            style={{
              position: "relative",
              top: -1,
            }}
            icon={
              theme.direction === "ltr" ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )
            }
            size="small"
          />
          <Box
            sx={{
              ml: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ButtonAntd
              type="primary"
              danger
              onClick={handleNavigate}
              style={{
                position: "relative",
                top: -1,
              }}
              icon={<HomeOutlined />}
              size="small"
            />
          </Box>
        </DrawerHeader>
        <Divider />
        <List
          sx={{
            p: 0,
            width: width === "xs" ? drawerWidth : "auto",
            overflowY: "auto",
            overflowX: "hidden",
            height: `calc(100vh - ${
              Number(width === "xs" ? 130 : NAVBAR_HEIGHT) + 2
            }px)`,
            minHeight: `calc(100vh - ${
              Number(width === "xs" ? 130 : NAVBAR_HEIGHT) + 2
            }px)`,
            maxHeight: `calc(100vh - ${
              Number(width === "xs" ? 130 : NAVBAR_HEIGHT) + 2
            }px)`,
          }}
        >
          <DrawerStateContext.Provider value={contextValue}>
            {modules.map((el) => (
              <DrawerItem key={`module_${el.id}`} data={el} open={open} />
            ))}
          </DrawerStateContext.Provider>
        </List>
      </>
    ),
    [
      modules,
      open,
      width,
      theme.direction,
      contextValue,
      handleClose,
      handleNavigate,
    ]
  );

  if (width === "xs") {
    return (
      <MuiDrawer open={open} onClose={handleClose}>
        {BodyDrawer}
      </MuiDrawer>
    );
  }

  return (
    <DrawerEl variant="permanent" open={open}>
      {BodyDrawer}
    </DrawerEl>
  );
};

// OPTIMIZACIÓN: Memorizar DrawerItem
export const DrawerItem = React.memo<PropsI>(
  ({ data, open }) => {
    const {
      id,
      name,
      nameSort,
      icon,
      iconLibrary,
      iconActive,
      iconActiveLibrary,
      children,
    } = data;

    const location = useLocation();
    const { toggleModule, isModuleExpanded, expandingModule } =
      React.useContext(DrawerStateContext);

    const openOption = isModuleExpanded(id);
    const isCurrentlyExpanding = expandingModule === id;

    // OPTIMIZACIÓN: Memoizar verificación de hijo activo
    const isChildActive = React.useMemo(() => {
      return (
        children?.some(
          (child) => child.to && location.pathname.startsWith(child.to)
        ) ?? false
      );
    }, [children, location.pathname]);

    const onClickOption = React.useCallback(
      () => toggleModule(id),
      [toggleModule, id]
    );

    if (!children || children.length === 0) {
      return <DrawerItemChildren data={data} padding={0} open={open} />;
    }

    return (
      <>
        <StyledModuleItem
          key={`${id}_${name}_${nameSort}`}
          disablePadding
          divider
          isExpanded={openOption}
          isChildActive={isChildActive}
        >
          <ListItemButton
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              flexWrap: "wrap",
              height: 55,
              position: "relative",
              overflow: "hidden",

              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                transition: "left 0.5s",
              },

              "&:hover::before": {
                left: "100%",
              },
            }}
            onClick={onClickOption}
          >
            <ListItemIcon>
              <Zoom in={true} timeout={300}>
                <div>
                  <DynamicIcon
                    icon={openOption || isChildActive ? iconActive : icon}
                    lib={
                      openOption || isChildActive
                        ? iconActiveLibrary
                        : iconLibrary
                    }
                    color={openOption || isChildActive ? "#fff" : COLOR_PRIMARY}
                    size={open ? 20 : 16}
                  />
                </div>
              </Zoom>
            </ListItemIcon>
            <ListItemText
              disableTypography
              sx={{
                fontSize: getImprovedFontSize(nameSort, open),
                fontWeight: open ? "bolder" : 600,
                position: "relative",
                left: open ? -30 : 0,
                transition: "all 0.3s ease",
              }}
              primary={nameSort}
            />
            {open && (
              <ExpandIcon isExpanded={openOption}>
                <Fade in={true} timeout={500}>
                  <div>
                    {openOption ? (
                      <ExpandLess sx={{ color: "inherit", fontSize: 20 }} />
                    ) : (
                      <ExpandMore sx={{ color: "inherit", fontSize: 20 }} />
                    )}
                  </div>
                </Fade>
              </ExpandIcon>
            )}
          </ListItemButton>
        </StyledModuleItem>

        <Collapse
          in={openOption}
          timeout={{
            enter: 500,
            exit: 300,
          }}
          easing={{
            enter: "cubic-bezier(0.4, 0, 0.2, 1)",
            exit: "cubic-bezier(0.4, 0, 0.6, 1)",
          }}
          unmountOnExit
        >
          <StyledChildrenContainer>
            <List
              component="div"
              disablePadding
              sx={{
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                borderLeft: `3px solid ${COLOR_PRIMARY}`,
                position: "relative",

                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  background: `linear-gradient(90deg, ${COLOR_PRIMARY}, transparent)`,
                  animation: isCurrentlyExpanding
                    ? `${expandEffect} 0.5s ease-out`
                    : "none",
                },
              }}
            >
              {children.map((el, index) => (
                <Fade
                  key={`${el.id}_${el.code}`}
                  in={openOption}
                  timeout={300 + index * 100}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <div>
                    <DrawerItemChildren data={el} padding={0.5} open={open} />
                  </div>
                </Fade>
              ))}
            </List>
          </StyledChildrenContainer>
        </Collapse>
      </>
    );
  },
  (prevProps, nextProps) => {
    // OPTIMIZACIÓN: Comparación shallow personalizada
    return (
      prevProps.data.id === nextProps.data.id &&
      prevProps.open === nextProps.open &&
      JSON.stringify(prevProps.data.children) ===
        JSON.stringify(nextProps.data.children)
    );
  }
);

// OPTIMIZACIÓN: Memorizar DrawerItemChildren
export const DrawerItemChildren = React.memo<PropsI>(
  ({ data, padding, open }) => {
    const {
      id,
      to,
      name,
      nameSort,
      icon,
      iconLibrary,
      iconActive,
      iconActiveLibrary,
      options,
      parentModuleId,
    } = data;

    return (
      <ListItem
        key={`${id}_${name}_${nameSort}`}
        disablePadding
        sx={{
          pl: padding,
          borderBottom: options ? "1px solid gainsboro" : "none",
          background: parentModuleId && options ? "#E5E7E9" : "white",
          height: parentModuleId && options ? 50 : 55,
          transition: "all 0.2s ease-in-out",

          "&:hover": {
            backgroundColor: "#f0f2f5",
            transform: "translateX(5px)",
            borderLeft: `3px solid ${COLOR_PRIMARY}`,
          },
        }}
      >
        <NavLink to={to!} style={{ width: "100%", textDecoration: "none" }}>
          {({ isActive, isPending }) => (
            <div className={isActive ? "navlinkA" : "navlink"}>
              <ListItemButton
                className="list-item-button"
                selected={isActive}
                sx={{
                  transition: "all 0.2s ease-in-out",
                  borderRadius: isActive ? "8px" : "0px",
                  margin: isActive ? "2px" : "0px",

                  "&.Mui-selected": {
                    backgroundColor: COLOR_PRIMARY + "20",
                    "&:hover": {
                      backgroundColor: COLOR_PRIMARY + "30",
                    },
                  },
                }}
              >
                <ListItemIcon>
                  <Zoom in={true} timeout={200}>
                    <div>
                      <DynamicIcon
                        icon={isActive ? iconActive : icon}
                        lib={isActive ? iconActiveLibrary : iconLibrary}
                        color={isActive ? "#fff" : COLOR_PRIMARY}
                        size={open ? 20 : 16}
                      />
                    </div>
                  </Zoom>
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  sx={{
                    fontSize: getImprovedFontSize(nameSort, open),
                    fontWeight: open ? "bolder" : 600,
                    position: "relative",
                    left: open ? -30 : 0,
                    transition: "all 0.2s ease-in-out",
                  }}
                  primary={nameSort}
                />
              </ListItemButton>
            </div>
          )}
        </NavLink>
      </ListItem>
    );
  },
  (prevProps, nextProps) => {
    // OPTIMIZACIÓN: Comparación específica para evitar re-renders
    return (
      prevProps.data.id === nextProps.data.id &&
      prevProps.open === nextProps.open &&
      prevProps.padding === nextProps.padding
    );
  }
);


const getImprovedFontSize = (text: string, isOpen: boolean): number => {
  const length = text.length;
  
  if (isOpen) {
    // Transición suave: 12 → 11 → 10 → 9 → 8 → 7
    if (length <= 6) return 12;       
    if (length <= 8) return 11;       
    if (length <= 10) return 10;      
    if (length <= 13) return 9;       
    if (length <= 17) return 8;       
    return 7;                         
  } else {
    // Transición suave: 10 → 9 → 8 → 7 → 6 → 5
    if (length <= 6) return 10;       
    if (length <= 8) return 9;        
    if (length <= 10) return 8;       
    if (length <= 13) return 7;       
    if (length <= 17) return 6;       
    return 5;                         
  }
};